import { isAxiosError } from "axios";
import { apiClient } from "@/services/apiClient";
import type { NotarialRequestEventDto } from "@/services/notaryRequestsService";
import type { UserAddressCreatePayload } from "@/services/userAddressesService";
import {
  getMyNotarialRequestDetail,
  type IncomeCertificateDetailsBody,
  type RequestDocumentSlot,
  type UserNotarialRequestDetail,
  type UserNotarialRequestDocument,
  type UserNotarialRequestDocumentType,
  type UserNotarialRequestSummary,
  type UserRequestPayment,
} from "@/services/userNotarialRequestsService";

export type UserAccountantRequestType = "income_certificate";

export type UserAccountantRequestSummary = Omit<
  UserNotarialRequestSummary,
  "requestType"
> & {
  requestType: string;
  fulfillmentMode?: string;
};

export interface UserAccountantRequestDetail
  extends UserAccountantRequestSummary {
  requestedByUserId: string;
  acceptedAt: string | null;
  certificatePathChoice: string | null;
  missingInfoMessage: string | null;
  missingDocumentNotes: Record<string, string> | null;
  certificateNotes?: string | null;
  cannotCompleteNotes: string | null;
  payment?: UserRequestPayment | null;
  pickupAddressSnapshot: string | null;
  deliveryAddressId: string | null;
  deliveryAddressSnapshot: string | null;
  deliveryContactPhone: string | null;
  deliveryContactName: string | null;
  deliveryNotes: string | null;
  events: NotarialRequestEventDto[];
}

export type ConsumerCertificateRequestSource =
  | "notarial"
  | "accountant"
  | "quote";

export type ConsumerCertificateRequestDetail =
  | UserNotarialRequestDetail
  | UserAccountantRequestDetail;

/** quote_request has no catalog product / fulfillment fields yet — matches mapQuoteRequestToConsumerListDto on the backend. */
export interface QuoteRequestConsumerListItem {
  id: string;
  status: string;
  title: string;
  discipline: string;
  /**
   * Delivered questions from professionals still waiting on this consumer.
   * The status does not move when one arrives — an RFQ with a question
   * pending still reads "recibiendo cotizaciones" — so the list needs it
   * separately or the request looks like it needs nothing.
   */
  unansweredQuestionCount: number;
  /**
   * Live bids waiting to be reviewed. Also not expressible by `status` — an
   * RFQ reads "recibiendo cotizaciones" whether three have arrived or none.
   */
  submittedQuoteCount: number;
  /**
   * Each leg's quoting window. The row needs them to tell a lapsed RFQ from
   * a live one: `status` stays 'quoting' through the whole award grace, and
   * isQuotingClosed requires EVERY leg to have closed. Absent before the
   * backend carried them, which is why the helper's parameter is optional.
   */
  legs?: Array<{ quotingExpiresAt: string | null }>;
  acceptedAt: string | null;
  estimatedCompletionDate: string | null;
  certificateValidityDate: string | null;
  /**
   * A correction the requester asked for after delivery. The request stays
   * "completed" while one is open, so this is served rather than inferred
   * from status.
   */
  correctionRequestedAt: string | null;
  correctionReason: string | null;
  correctionResolvedAt: string | null;
  correctionOpen: boolean;
  createdAt: string;
  updatedAt: string;
}

export type MergedConsumerRequestSummary =
  | ({ source: "notarial" } & UserNotarialRequestSummary)
  | ({ source: "accountant" } & UserAccountantRequestSummary)
  | ({ source: "quote" } & QuoteRequestConsumerListItem);

export async function listMergedMyCertificateRequests(): Promise<
  MergedConsumerRequestSummary[]
> {
  const { data } = await apiClient.get<{
    requests: Array<
      | ({ channel: "notary" } & UserNotarialRequestSummary)
      | ({ channel: "accountant" } & UserAccountantRequestSummary)
      | ({ channel: "quote" } & QuoteRequestConsumerListItem)
    >;
  }>("/me/requests");

  const mapped = data.requests.map((row): MergedConsumerRequestSummary => {
    const { channel, ...rest } = row;
    if (channel === "notary")
      return { ...rest, source: "notarial" } as MergedConsumerRequestSummary;
    if (channel === "accountant")
      return { ...rest, source: "accountant" } as MergedConsumerRequestSummary;
    return { ...rest, source: "quote" } as MergedConsumerRequestSummary;
  });

  return mapped;
}

export function listMyAccountantRequests(): Promise<
  UserAccountantRequestSummary[]
> {
  return apiClient
    .get<UserAccountantRequestSummary[]>("/me/accountant-requests")
    .then((res) => res.data);
}

export function createMyAccountantRequest(params: {
  type: string;
  checkoutId?: string;
  companySnapshot?: {
    legalName?: string;
    tradeName?: string;
    rut?: string;
    legalAddress?: string;
    city?: string;
    department?: string;
    postalCode?: string;
    phone?: string;
    email?: string;
    fechaInicio?: string;
    economicActivity?: string;
  };
  incomeCertificateDetails?: IncomeCertificateDetailsBody;
  fulfillmentMode?: string;
  invoiceIncludeRut?: boolean;
  invoiceRutDigits?: string;
  companyRutUnverified?: boolean;
}): Promise<{
  id: string;
  status: string;
  requestedByUserId: string;
  createdAt: string;
}> {
  return apiClient
    .post<{
      id: string;
      status: string;
      requestedByUserId: string;
      createdAt: string;
    }>("/me/accountant-requests", {
      type: params.type,
      checkoutId: params.checkoutId,
      companySnapshot: params.companySnapshot,
      incomeCertificateDetails: params.incomeCertificateDetails,
      fulfillmentMode: params.fulfillmentMode,
      invoiceIncludeRut: params.invoiceIncludeRut,
      invoiceRutDigits: params.invoiceRutDigits,
      ...(params.companyRutUnverified === true && {
        companyRutUnverified: true,
      }),
    })
    .then((res) => res.data);
}

export function uploadMyAccountantRequestDocument(
  requestId: string,
  documentType: UserNotarialRequestDocumentType,
  file: File,
): Promise<UserNotarialRequestDocument> {
  const formData = new FormData();
  formData.append("documentType", documentType);
  formData.append("file", file);
  return apiClient
    .post<UserNotarialRequestDocument>(
      `/me/accountant-requests/${requestId}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export function getMyAccountantRequestDocumentSlots(
  requestId: string,
): Promise<{ documents: RequestDocumentSlot[] }> {
  return apiClient
    .get<{
      documents: RequestDocumentSlot[];
    }>(`/me/accountant-requests/${requestId}/document-requests`)
    .then((res) => res.data);
}

/** The accountant twin of the notarial slot upload — same contract, same reasoning. */
export function uploadMyAccountantRequestDocumentForSlot(
  requestId: string,
  documentRequestId: string,
  file: File,
): Promise<UserNotarialRequestDocument> {
  const formData = new FormData();
  formData.append("documentRequestId", documentRequestId);
  formData.append("file", file);
  return apiClient
    .post<UserNotarialRequestDocument>(
      `/me/accountant-requests/${requestId}/documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export function getMyAccountantRequestDetail(
  id: string,
): Promise<UserAccountantRequestDetail> {
  return apiClient
    .get<UserAccountantRequestDetail>(`/me/accountant-requests/${id}`)
    .then((res) => res.data);
}

export function parseCertificateRequestSourceParam(
  value: string | null,
): ConsumerCertificateRequestSource | null {
  if (value === "accountant" || value === "notarial") {
    return value;
  }
  return null;
}

export async function getMyConsumerCertificateRequestDetail(
  id: string,
  options?: { source?: ConsumerCertificateRequestSource | null },
): Promise<{
  detail: ConsumerCertificateRequestDetail;
  source: ConsumerCertificateRequestSource;
}> {
  const hint = options?.source ?? null;

  if (hint === "accountant") {
    try {
      const detail = await getMyAccountantRequestDetail(id);
      return { detail, source: "accountant" };
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) {
        const detail = await getMyNotarialRequestDetail(id);
        return { detail, source: "notarial" };
      }
      throw e;
    }
  }

  if (hint === "notarial") {
    try {
      const detail = await getMyNotarialRequestDetail(id);
      return { detail, source: "notarial" };
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) {
        const detail = await getMyAccountantRequestDetail(id);
        return { detail, source: "accountant" };
      }
      throw e;
    }
  }

  try {
    const detail = await getMyNotarialRequestDetail(id);
    return { detail, source: "notarial" };
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) {
      const detail = await getMyAccountantRequestDetail(id);
      return { detail, source: "accountant" };
    }
    throw e;
  }
}

export function cancelMyAccountantRequest(id: string): Promise<void> {
  return apiClient
    .delete(`/me/accountant-requests/${id}`)
    .then(() => undefined);
}

/**
 * Ask for the delivered document to be corrected. Reopens the professional's
 * upload path with no time limit until they answer it.
 */
export function requestMyAccountantRequestCorrection(
  requestId: string,
  reason: string,
): Promise<void> {
  return apiClient
    .post(`/me/accountant-requests/${requestId}/request-correction`, {
      reason: reason.trim(),
    })
    .then(() => undefined);
}

export function resubmitMyAccountantRequest(
  requestId: string,
  notes?: string,
): Promise<void> {
  return apiClient
    .post(`/me/accountant-requests/${requestId}/resubmit`, {
      notes: notes?.trim() ? notes.trim() : undefined,
    })
    .then(() => undefined);
}

export function deleteMyAccountantRequestDocument(
  requestId: string,
  documentId: string,
): Promise<void> {
  return apiClient
    .delete(`/me/accountant-requests/${requestId}/documents/${documentId}`)
    .then(() => undefined);
}

export function fetchMyAccountantRequestDocumentBlob(
  requestId: string,
  documentId: string,
): Promise<Blob> {
  return apiClient
    .get<Blob>(
      `/me/accountant-requests/${encodeURIComponent(requestId)}/documents/${encodeURIComponent(documentId)}/file`,
      { responseType: "blob" },
    )
    .then((res) => res.data);
}

export function submitMyAccountantRequestDeliveryDetails(
  requestId: string,
  body: {
    deliveryAddressId?: string;
    newDeliveryAddress?: UserAddressCreatePayload;
    deliveryContactPhone?: string;
    deliveryContactName?: string;
    deliveryNotes?: string;
  },
): Promise<{ id: string; status: string }> {
  return apiClient
    .patch<{
      id: string;
      status: string;
    }>(`/me/accountant-requests/${encodeURIComponent(requestId)}/delivery-details`, body)
    .then((res) => res.data);
}

/** What a bulk upload returns: what stored, and what did not. */
export interface BulkDocumentUploadResult {
  stored: Array<{ id: string; documentType: string; originalFilename: string }>;
  failed: Array<{ filename: string; reason: string }>;
}

/**
 * Several documents in ONE request, with one note applied to all of them.
 *
 * Deliberately not a loop over the single-file endpoint: that would produce a
 * timeline row and a professional notification PER FILE (spec
 * bulk-document-upload-with-note-as-built).
 *
 * Partial success is normal here, not exceptional — the response names what
 * failed and the caller must surface it rather than assuming everything
 * landed.
 */
export function uploadMyRequestDocumentsBulk(
  requestId: string,
  documentType: string,
  files: File[],
  note?: string | null,
): Promise<BulkDocumentUploadResult> {
  const formData = new FormData();
  formData.append("documentType", documentType);
  if (note != null && note.trim() !== "") {
    formData.append("note", note.trim());
  }
  for (const file of files) {
    formData.append("files", file);
  }
  return apiClient
    .post<BulkDocumentUploadResult>(
      `/me/accountant-requests/${requestId}/documents/bulk`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}
